// Where you stand this month, before the payslip tells you.
//
// The attendance policy holds or deducts pay for late arrivals, and charges for
// missing a monthly hours target. Until this card, none of that was visible
// anywhere: the numbers existed only inside a payroll run. An employee's net
// simply came out smaller. The policy announcement even asks people to "track
// your monthly hours regularly so that any shortfall can be covered in time" —
// this is the thing that sentence assumed existed.
//
// Every figure comes from the real payroll calculation (GET
// /attendance/my_policy_status), not a second implementation, so the card and
// the payslip cannot disagree.
//
// Tone matters here. This card tells people they are losing money, so it leads
// with what they can still act on — how far ahead or behind pace they are — and
// keeps the worst-case rupee figure secondary and explicitly labelled. Mid-month
// the projection is always the whole remaining gap, which read on its own looks
// like a threat rather than information.

import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, TrendingDown } from 'lucide-react';
import { getMyPolicyStatus, type PolicyStatus } from '../api/attendance';

const money = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const hrs = (n: number) => `${Number(n || 0).toFixed(1)}h`;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// "2026-08" -> "August". Named months rather than dates because the employee
// thinks in months here ("last month's money"), not in ISO stamps.
const monthName = (stamp: string) => {
  const [, m] = stamp.split('-');
  return MONTHS[Number(m) - 1] ?? stamp;
};

const monthList = (stamps: string[]) => {
  const names = stamps.map(monthName);
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

interface Props {
  /** Defaults to the current month. */
  year?: number;
  month?: number;
  /** Admin/payroll viewing someone else; omit for your own row. */
  employeeId?: string;
}

export default function MonthPolicyStatus({ year, month, employeeId }: Props) {
  const [data, setData] = useState<PolicyStatus | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    getMyPolicyStatus(year, month, employeeId)
      .then((res) => { if (!cancelled) setData(res); })
      // Informational card — a failure must never take the attendance page
      // down with it.
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [year, month, employeeId]);

  if (failed || !data) return null;

  const { late, hours } = data;
  const label = `${MONTHS[data.month - 1]} ${data.year}`;

  // Nothing is configured for this employee — no allowance to run out of and no
  // hours target. Showing an empty card would just be noise.
  if (!late.free_days && late.action === 'none' && !hours.enabled) return null;

  const charged = late.charged_days > 0;
  const behind = hours.enabled && !hours.on_track;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-gray-900">This month</h3>
        <span className="text-xs text-gray-500">
          {label}
          {data.is_current_month && ' · so far'}
        </span>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {/* ---------------- late coming ---------------- */}
        <section>
          <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
            <Clock size={14} /> Late coming
          </div>

          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-semibold text-gray-900">{late.count}</span>
            <span className="text-sm text-gray-500">
              late {late.count === 1 ? 'day' : 'days'}
            </span>
          </div>

          {late.free_days > 0 && (
            <p className="mt-0.5 text-xs text-gray-600">
              {late.free_left > 0
                ? `${late.free_left} of ${late.free_days} free ${late.free_left === 1 ? 'day' : 'days'} left`
                : `All ${late.free_days} free days used`}
            </p>
          )}

          {late.late_after && (
            <p className="mt-0.5 text-xs text-gray-400">
              Counted after {late.late_after}
            </p>
          )}

          {charged && (
            <div
              className={`mt-2 rounded-lg border p-2 text-xs ${
                late.action === 'deduct'
                  ? 'border-red-200 bg-red-50 text-red-900'
                  : 'border-amber-200 bg-amber-50 text-amber-900'
              }`}
            >
              <p className="font-semibold">
                {money(late.amount)}{' '}
                {late.action === 'deduct' ? 'being deducted' : 'on hold'}
              </p>
              <p className="mt-0.5">
                {late.charged_days} × half a day&apos;s pay.{' '}
                {late.action === 'hold'
                  ? 'Returned if next month stays within the free days.'
                  : 'Taken from this month’s salary.'}
              </p>
            </div>
          )}

          {/* The honest version of "you have N left": past the threshold the
              next late day can cost far more than the last one did. */}
          {late.next_day_cost.amount > 0 && (
            <p className="mt-1.5 text-xs text-gray-500">
              One more late day:{' '}
              <span className="font-medium text-gray-700">
                +{money(late.next_day_cost.amount)}
              </span>
            </p>
          )}

          {/* Money from earlier months that has not come back yet. A hold is a
              promise to repay; without a visible balance it reads as money
              quietly kept, and that is what people escalate about. */}
          {late.held_earlier.amount > 0 && (
            <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-2 text-xs text-gray-700">
              <p>
                <span className="font-semibold">
                  {money(late.held_earlier.amount)}
                </span>{' '}
                from {monthList(late.held_earlier.months)} still on hold
              </p>
              {late.held_earlier.release_if_lates_lte != null && (
                <p className="mt-0.5 text-gray-500">
                  Released if this month stays within{' '}
                  {late.held_earlier.release_if_lates_lte} late{' '}
                  {late.held_earlier.release_if_lates_lte === 1 ? 'day' : 'days'}.
                </p>
              )}
            </div>
          )}
        </section>

        {/* ---------------- monthly hours ---------------- */}
        {hours.enabled ? (
          <section>
            <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-600">
              {behind ? <TrendingDown size={14} /> : <CheckCircle2 size={14} />} Monthly hours
            </div>

            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-semibold text-gray-900">
                {hrs(hours.worked)}
              </span>
              <span className="text-sm text-gray-500">of {hrs(hours.required)}</span>
            </div>

            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
              <div
                className={`h-full rounded-full ${behind ? 'bg-amber-400' : 'bg-emerald-500'}`}
                style={{
                  width: `${Math.min(100, (hours.worked / (hours.required || 1)) * 100)}%`,
                }}
              />
            </div>

            {/* Pace, not the raw gap — this is the number someone can act on. */}
            <p className="mt-1.5 text-xs text-gray-600">
              {hours.on_track ? (
                <span className="text-emerald-700">
                  On track — {hrs(Math.abs(hours.pace))} ahead of pace
                </span>
              ) : (
                <span className="text-amber-700">
                  {hrs(Math.abs(hours.pace))} behind pace
                </span>
              )}
            </p>

            {behind && hours.projected_deduction > 0 && (
              <div className="mt-2 flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                <p>
                  If the month ended today you&apos;d be {hrs(hours.shortfall)} short —
                  about <span className="font-semibold">{money(hours.projected_deduction)}</span>.
                  {data.is_current_month && ' There is still time to make it up.'}
                </p>
              </div>
            )}
          </section>
        ) : (
          <section className="text-xs text-gray-400">
            <div className="mb-1 flex items-center gap-1.5 font-medium text-gray-600">
              <Clock size={14} /> Monthly hours
            </div>
            <p>{hrs(hours.worked)} worked. No monthly target applies.</p>
          </section>
        )}
      </div>
    </div>
  );
}
